import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, 
  Settings, 
  Bell, 
  Shield, 
  Save, 
  Edit, 
  Camera,
  Brain,
  RefreshCw,
  Download,
  Share2,
  Filter,
  Calendar,
  Clock,
  Target,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Info
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useProfile } from '../context/ProfileContext';
import { useInsights } from '../context/InsightsContext';
import InsightCard from '../components/insights/InsightCard';
import CycleStatsOverview from '../components/insights/CycleStatsOverview';

const Profile: React.FC = () => {
  const { t } = useTranslation();
  const { user, signOut } = useAuth();
  const { profile, preferences, loading, updateProfile, updatePreferences } = useProfile();
  const { insights, generatedInsights, cycleStats, unreadCount, refreshInsights, loading: insightsLoading } = useInsights();
  const [activeTab, setActiveTab] = useState('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [insightFilter, setInsightFilter] = useState<'all' | 'high' | 'medium' | 'low'>('all');

  const [profileForm, setProfileForm] = useState({
    full_name: profile?.full_name || '',
    date_of_birth: profile?.date_of_birth || '',
    language_preference: profile?.language_preference || 'en',
    timezone: profile?.timezone || 'UTC',
  });

  const [preferencesForm, setPreferencesForm] = useState({
    notification_period_reminder: preferences?.notification_period_reminder ?? true,
    notification_ovulation_reminder: preferences?.notification_ovulation_reminder ?? true,
    notification_doctor_response: preferences?.notification_doctor_response ?? true,
    cycle_length_average: preferences?.cycle_length_average || 28,
    period_length_average: preferences?.period_length_average || 5,
    privacy_data_sharing: preferences?.privacy_data_sharing ?? false,
    privacy_analytics: preferences?.privacy_analytics ?? true,
  });

  const handleProfileSave = async () => {
    try {
      setSaving(true);
      await updateProfile(profileForm);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePreferencesSave = async () => {
    try {
      setSaving(true);
      await updatePreferences(preferencesForm);
      // Refresh insights after updating preferences to recalculate predictions
      await refreshInsights();
    } catch (error) {
      console.error('Error updating preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshInsights = async () => {
    await refreshInsights();
  };

  const filteredInsights = generatedInsights.filter(insight => 
    insightFilter === 'all' || insight.priority === insightFilter
  );

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'cycle-preferences', label: 'Cycle Settings', icon: Calendar },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'privacy', label: 'Privacy', icon: Shield },
    { id: 'insights', label: `Insights ${unreadCount > 0 ? `(${unreadCount})` : ''}`, icon: Brain },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <motion.div
          className="w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
        />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      className="max-w-6xl mx-auto"
    >
      <div className="mb-8">
        <motion.h1 
          className="text-3xl font-bold text-gray-900 mb-2"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          Profile Settings
        </motion.h1>
        <motion.p 
          className="text-gray-600"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Manage your account settings, cycle preferences, and view personalized health insights
        </motion.p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <motion.div 
          className="lg:col-span-1"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <div className="text-center mb-6">
              <motion.div
                className="relative w-20 h-20 mx-auto mb-4"
                whileHover={{ scale: 1.05 }}
              >
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <User className="w-10 h-10 text-white" />
                </div>
                <motion.button
                  className="absolute bottom-0 right-0 w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Camera className="w-3 h-3 text-gray-600" />
                </motion.button>
              </motion.div>
              <h3 className="font-semibold text-gray-900">{profile?.full_name || 'User'}</h3>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>

            <nav className="space-y-2">
              {tabs.map((tab) => (
                <motion.button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center space-x-3 px-4 py-3 rounded-xl transition-all duration-300 ${
                    activeTab === tab.id
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-gray-600 hover:bg-purple-50 hover:text-purple-600'
                  }`}
                  whileHover={{ x: 4 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="text-sm">{tab.label}</span>
                  {tab.id === 'insights' && unreadCount > 0 && (
                    <motion.span
                      className="ml-auto w-2 h-2 bg-red-500 rounded-full"
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                  )}
                </motion.button>
              ))}
            </nav>
          </div>
        </motion.div>

        {/* Content */}
        <motion.div 
          className="lg:col-span-3"
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6">
            <AnimatePresence mode="wait">
              {activeTab === 'profile' && (
                <motion.div
                  key="profile"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-semibold text-gray-900">Profile Information</h2>
                    <motion.button
                      onClick={() => setIsEditing(!isEditing)}
                      className="flex items-center space-x-2 px-4 py-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <Edit className="w-4 h-4" />
                      <span>{isEditing ? 'Cancel' : 'Edit'}</span>
                    </motion.button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name
                      </label>
                      {isEditing ? (
                        <input
                          type="text"
                          value={profileForm.full_name}
                          onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-3 bg-gray-50 rounded-xl">{profile?.full_name || 'Not set'}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Date of Birth
                      </label>
                      {isEditing ? (
                        <input
                          type="date"
                          value={profileForm.date_of_birth}
                          onChange={(e) => setProfileForm({ ...profileForm, date_of_birth: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      ) : (
                        <p className="px-4 py-3 bg-gray-50 rounded-xl">
                          {profile?.date_of_birth ? new Date(profile.date_of_birth).toLocaleDateString() : 'Not set'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Language Preference
                      </label>
                      {isEditing ? (
                        <select
                          value={profileForm.language_preference}
                          onChange={(e) => setProfileForm({ ...profileForm, language_preference: e.target.value })}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        >
                          <option value="en">English</option>
                          <option value="si">සිංහල</option>
                          <option value="ta">தமிழ்</option>
                        </select>
                      ) : (
                        <p className="px-4 py-3 bg-gray-50 rounded-xl">
                          {profile?.language_preference === 'en' ? 'English' : 
                           profile?.language_preference === 'si' ? 'සිංහල' : 'தமிழ்'}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email
                      </label>
                      <p className="px-4 py-3 bg-gray-50 rounded-xl text-gray-500">{user?.email}</p>
                    </div>
                  </div>

                  {isEditing && (
                    <motion.div 
                      className="flex justify-end space-x-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.button
                        onClick={handleProfileSave}
                        disabled={saving}
                        className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {saving ? (
                          <motion.div
                            className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                          />
                        ) : (
                          <Save className="w-4 h-4" />
                        )}
                        <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                      </motion.button>
                    </motion.div>
                  )}
                </motion.div>
              )}

              {activeTab === 'cycle-preferences' && (
                <motion.div
                  key="cycle-preferences"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-900">Cycle Preferences</h2>
                      <p className="text-sm text-gray-600 mt-1">These settings affect all predictions and calendar displays</p>
                    </div>
                    <motion.div
                      className="flex items-center space-x-2 px-3 py-2 bg-purple-100 text-purple-700 rounded-full text-sm"
                      animate={{ scale: [1, 1.05, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Target className="w-4 h-4" />
                      <span>Affects Predictions</span>
                    </motion.div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <motion.div
                      className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <Calendar className="w-5 h-5 text-purple-600" />
                        <label className="block text-sm font-medium text-purple-900">
                          Average Cycle Length (days)
                        </label>
                      </div>
                      <input
                        type="number"
                        min="21"
                        max="35"
                        value={preferencesForm.cycle_length_average}
                        onChange={(e) => setPreferencesForm({ 
                          ...preferencesForm, 
                          cycle_length_average: parseInt(e.target.value) 
                        })}
                        className="w-full px-4 py-3 border border-purple-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
                      />
                      <p className="text-xs text-purple-700 mt-2">
                        📊 Used for predicting next period and ovulation dates
                      </p>
                    </motion.div>

                    <motion.div
                      className="p-4 bg-gradient-to-r from-pink-50 to-rose-50 rounded-xl border border-pink-200"
                      whileHover={{ scale: 1.02 }}
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <Clock className="w-5 h-5 text-pink-600" />
                        <label className="block text-sm font-medium text-pink-900">
                          Average Period Length (days)
                        </label>
                      </div>
                      <input
                        type="number"
                        min="3"
                        max="7"
                        value={preferencesForm.period_length_average}
                        onChange={(e) => setPreferencesForm({ 
                          ...preferencesForm, 
                          period_length_average: parseInt(e.target.value) 
                        })}
                        className="w-full px-4 py-3 border border-pink-300 rounded-xl focus:ring-2 focus:ring-pink-500 focus:border-transparent bg-white"
                      />
                      <p className="text-xs text-pink-700 mt-2">
                        🩸 Used for calendar period day predictions
                      </p>
                    </motion.div>
                  </div>

                  {/* Advanced Prediction Settings */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                    <h3 className="text-lg font-medium text-blue-900 mb-4 flex items-center">
                      <TrendingUp className="w-5 h-5 mr-2" />
                      Advanced Prediction Settings
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">
                          Prediction Method
                        </label>
                        <select className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                          <option value="average">Average-based (Recommended)</option>
                          <option value="pattern">Pattern Recognition</option>
                          <option value="hybrid">Hybrid Method</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-blue-800 mb-2">
                          Prediction Confidence
                        </label>
                        <select className="w-full px-4 py-3 border border-blue-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white">
                          <option value="conservative">Conservative (±2 days)</option>
                          <option value="standard">Standard (±1 day)</option>
                          <option value="precise">Precise (exact date)</option>
                        </select>
                      </div>
                    </div>

                    <div className="mt-4 p-3 bg-blue-100 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                        <div>
                          <p className="text-sm font-medium text-blue-900">How this affects your experience:</p>
                          <ul className="text-xs text-blue-800 mt-1 space-y-1">
                            <li>• Calendar predictions will use your custom cycle length</li>
                            <li>• Period reminders will be based on your preferences</li>
                            <li>• Ovulation predictions will be more accurate</li>
                            <li>• Health insights will consider your unique patterns</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <motion.button
                    onClick={handlePreferencesSave}
                    disabled={saving}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {saving ? (
                      <motion.div
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{saving ? 'Saving & Updating Predictions...' : 'Save Preferences & Update Predictions'}</span>
                  </motion.button>

                  {/* Current Predictions Preview */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                    <h3 className="text-lg font-medium text-green-900 mb-4 flex items-center">
                      <CheckCircle className="w-5 h-5 mr-2" />
                      Current Predictions Preview
                    </h3>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-sm text-green-700 font-medium">Next Period</p>
                        <p className="text-lg font-bold text-green-900">
                          {preferencesForm.cycle_length_average} day cycle
                        </p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-sm text-green-700 font-medium">Period Duration</p>
                        <p className="text-lg font-bold text-green-900">
                          {preferencesForm.period_length_average} days
                        </p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <p className="text-sm text-green-700 font-medium">Ovulation</p>
                        <p className="text-lg font-bold text-green-900">
                          Day {Math.round(preferencesForm.cycle_length_average / 2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {activeTab === 'notifications' && (
                <motion.div
                  key="notifications"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-semibold text-gray-900">Notification Preferences</h2>

                  <div className="space-y-4">
                    <motion.div 
                      className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center space-x-3">
                        <Bell className="w-5 h-5 text-purple-600" />
                        <div>
                          <h3 className="font-medium text-purple-900">Period Reminders</h3>
                          <p className="text-sm text-purple-700">Get notified 1-2 days before your period</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferencesForm.notification_period_reminder}
                          onChange={(e) => setPreferencesForm({
                            ...preferencesForm,
                            notification_period_reminder: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
                      </label>
                    </motion.div>

                    <motion.div 
                      className="flex items-center justify-between p-4 bg-orange-50 rounded-xl border border-orange-200"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center space-x-3">
                        <Calendar className="w-5 h-5 text-orange-600" />
                        <div>
                          <h3 className="font-medium text-orange-900">Ovulation Reminders</h3>
                          <p className="text-sm text-orange-700">Get notified during your fertile window</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferencesForm.notification_ovulation_reminder}
                          onChange={(e) => setPreferencesForm({
                            ...preferencesForm,
                            notification_ovulation_reminder: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                      </label>
                    </motion.div>

                    <motion.div 
                      className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center space-x-3">
                        <User className="w-5 h-5 text-blue-600" />
                        <div>
                          <h3 className="font-medium text-blue-900">Doctor Response Notifications</h3>
                          <p className="text-sm text-blue-700">Get notified when doctors respond to your questions</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferencesForm.notification_doctor_response}
                          onChange={(e) => setPreferencesForm({
                            ...preferencesForm,
                            notification_doctor_response: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </motion.div>
                  </div>

                  <motion.button
                    onClick={handlePreferencesSave}
                    disabled={saving}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {saving ? (
                      <motion.div
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{saving ? 'Saving...' : 'Save Notification Preferences'}</span>
                  </motion.button>
                </motion.div>
              )}

              {activeTab === 'privacy' && (
                <motion.div
                  key="privacy"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <h2 className="text-xl font-semibold text-gray-900">Privacy Settings</h2>

                  <div className="space-y-4">
                    <motion.div 
                      className="flex items-center justify-between p-4 bg-red-50 rounded-xl border border-red-200"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center space-x-3">
                        <Shield className="w-5 h-5 text-red-600" />
                        <div>
                          <h3 className="font-medium text-red-900">Data Sharing</h3>
                          <p className="text-sm text-red-700">Allow anonymous data sharing for research purposes</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferencesForm.privacy_data_sharing}
                          onChange={(e) => setPreferencesForm({
                            ...preferencesForm,
                            privacy_data_sharing: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
                      </label>
                    </motion.div>

                    <motion.div 
                      className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200"
                      whileHover={{ scale: 1.01 }}
                    >
                      <div className="flex items-center space-x-3">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        <div>
                          <h3 className="font-medium text-green-900">Analytics</h3>
                          <p className="text-sm text-green-700">Help improve the app with usage analytics</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferencesForm.privacy_analytics}
                          onChange={(e) => setPreferencesForm({
                            ...preferencesForm,
                            privacy_analytics: e.target.checked
                          })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                      </label>
                    </motion.div>
                  </div>

                  <motion.button
                    onClick={handlePreferencesSave}
                    disabled={saving}
                    className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {saving ? (
                      <motion.div
                        className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    <span>{saving ? 'Saving...' : 'Save Privacy Settings'}</span>
                  </motion.button>
                </motion.div>
              )}

              {activeTab === 'insights' && (
                <motion.div
                  key="insights"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="space-y-6"
                >
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">Personalized Health Insights</h2>
                    <div className="flex items-center space-x-3">
                      {/* Filter */}
                      <select
                        value={insightFilter}
                        onChange={(e) => setInsightFilter(e.target.value as any)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="all">All Insights</option>
                        <option value="high">High Priority</option>
                        <option value="medium">Medium Priority</option>
                        <option value="low">Low Priority</option>
                      </select>

                      {/* Refresh Button */}
                      <motion.button
                        onClick={handleRefreshInsights}
                        disabled={insightsLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <RefreshCw className={`w-4 h-4 ${insightsLoading ? 'animate-spin' : ''}`} />
                        <span>Refresh</span>
                      </motion.button>

                      {unreadCount > 0 && (
                        <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full font-medium">
                          {unreadCount} new
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Cycle Stats Overview */}
                  {cycleStats && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <CycleStatsOverview stats={cycleStats} />
                    </motion.div>
                  )}
                  
                  {/* Insights List */}
                  {filteredInsights.length > 0 ? (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-medium text-gray-900">
                          AI-Generated Insights ({filteredInsights.length})
                        </h3>
                        <div className="flex items-center space-x-2">
                          <motion.button
                            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-purple-600 text-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <Download className="w-4 h-4" />
                            <span>Export</span>
                          </motion.button>
                          <motion.button
                            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-purple-600 text-sm"
                            whileHover={{ scale: 1.05 }}
                          >
                            <Share2 className="w-4 h-4" />
                            <span>Share</span>
                          </motion.button>
                        </div>
                      </div>

                      {filteredInsights.map((insight, index) => (
                        <InsightCard key={insight.id} insight={insight} index={index} />
                      ))}
                    </div>
                  ) : (
                    <motion.div 
                      className="text-center py-12"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                    >
                      <motion.div
                        className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-4"
                        whileHover={{ scale: 1.1, rotate: 10 }}
                      >
                        <Brain className="w-8 h-8 text-purple-500" />
                      </motion.div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">
                        {insightFilter === 'all' ? 'No insights available yet' : `No ${insightFilter} priority insights`}
                      </h3>
                      <p className="text-gray-500 mb-6">
                        {insightFilter === 'all' 
                          ? 'Start logging your cycle data to get personalized insights and recommendations.'
                          : `Try changing the filter to see insights of different priorities.`
                        }
                      </p>
                      {insightFilter === 'all' && (
                        <motion.div
                          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-100"
                          initial={{ scale: 0.95 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.2 }}
                        >
                          <h4 className="font-medium text-purple-900 mb-2">What you'll get with more data:</h4>
                          <ul className="text-sm text-purple-700 space-y-1">
                            <li>• Cycle regularity analysis with confidence scores</li>
                            <li>• Symptom pattern recognition and correlations</li>
                            <li>• Mood tracking insights by cycle phase</li>
                            <li>• Personalized health tips and recommendations</li>
                            <li>• Prediction accuracy reports and trends</li>
                            <li>• Flow pattern analysis and health assessments</li>
                          </ul>
                        </motion.div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Profile;